import React, { useState, useEffect, useMemo } from 'react';
import { getRelationColor } from './relationStyles';
import './Contacts.css';

const TeamForm = ({ team, contacts, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    member_ids: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name || '',
        description: team.description || '',
        member_ids: team.members_preview?.map(m => m.id) || []
      });
    }
  }, [team]);

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!searchQuery.trim()) return contacts;
    const query = searchQuery.toLowerCase();
    return contacts.filter(contact => 
      contact.name.toLowerCase().includes(query) ||
      (contact.relation && contact.relation.toLowerCase().includes(query))
    );
  }, [contacts, searchQuery]);

  // Get selected contacts that match search
  const selectedCount = formData.member_ids.length;
  const filteredSelectedCount = filteredContacts.filter(c => formData.member_ids.includes(c.id)).length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleMemberToggle = (contactId) => {
    setFormData(prev => ({
      ...prev,
      member_ids: prev.member_ids.includes(contactId)
        ? prev.member_ids.filter(id => id !== contactId)
        : [...prev.member_ids, contactId]
    }));
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredContacts.map(c => c.id);
    setFormData(prev => ({
      ...prev,
      member_ids: [...new Set([...prev.member_ids, ...filteredIds])]
    }));
  };

  const handleDeselectAllFiltered = () => {
    const filteredIds = new Set(filteredContacts.map(c => c.id));
    setFormData(prev => ({
      ...prev,
      member_ids: prev.member_ids.filter(id => !filteredIds.has(id))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Team name is required');
      return;
    }

    if (formData.member_ids.length === 0) {
      setError('Please select at least one team member');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save team');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-form-overlay" onClick={onClose}>
      <div className="contact-form-modal team-form-modal" onClick={e => e.stopPropagation()}>
        <div className="contact-form-header">
          <h3>{team ? 'Edit Team' : 'Create Team'}</h3>
          <button className="contact-form-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="contact-form-error">{error}</div>}

          <div className="contact-form-group">
            <label htmlFor="name">Team Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Sales Team, Family"
              required
            />
          </div>

          <div className="contact-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description for this team"
              rows="2"
            />
          </div>

          <div className="contact-form-group team-members-section">
            <div className="team-members-header">
              <label>
                Team Members 
                <span className="selected-count">({selectedCount} selected)</span>
              </label>
            </div>
            
            {/* Search Box */}
            <div className="team-search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-icon">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="team-search-input"
              />
              {searchQuery && (
                <button 
                  type="button" 
                  className="search-clear-btn"
                  onClick={() => setSearchQuery('')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="team-quick-actions">
              <button type="button" onClick={handleSelectAllFiltered} className="team-action-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                Select {searchQuery ? 'filtered' : 'all'}
              </button>
              <button type="button" onClick={handleDeselectAllFiltered} className="team-action-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                </svg>
                Clear {searchQuery ? 'filtered' : 'all'}
              </button>
              {searchQuery && (
                <span className="team-filter-info">
                  Showing {filteredContacts.length} of {contacts.length}
                </span>
              )}
            </div>
            
            {/* Members List */}
            <div className="team-members-list">
              {contacts.length === 0 ? (
                <div className="team-no-contacts">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <p>No contacts available</p>
                  <span>Add contacts first to create teams</span>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="team-no-results">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  <p>No contacts found</p>
                  <span>Try a different search term</span>
                </div>
              ) : (
                filteredContacts.map(contact => {
                  const isSelected = formData.member_ids.includes(contact.id);
                  return (
                    <div 
                      key={contact.id} 
                      className={`team-member-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleMemberToggle(contact.id)}
                    >
                      <div className="member-checkbox">
                        <div className={`checkbox-box ${isSelected ? 'checked' : ''}`}>
                          {isSelected && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div 
                        className="member-avatar"
                        style={{ backgroundColor: getRelationColor(contact.relation) }}
                      >
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="member-details">
                        <span className="member-name">{contact.name}</span>
                        {contact.relation && (
                          <span 
                            className="member-relation"
                            style={{ color: getRelationColor(contact.relation) }}
                          >
                            {contact.relation}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="contact-form-actions">
            <button type="button" className="contact-form-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="contact-form-submit" disabled={loading || selectedCount === 0}>
              {loading ? 'Saving...' : (team ? 'Update Team' : `Create Team (${selectedCount})`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamForm;
